import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PACKAGES = [
  { id: 'starter',  label: 'Starter',  price: '$6.00',  priceNum: 6.00,  credits: 485, highlight: false },
  // { id: 'standard', label: 'Standard', price: '$10.00', priceNum: 10.00, credits: 900, highlight: true  },
];

// ── Inner Stripe payment form (must be inside <Elements>) ──────────────────
function StripePaymentForm({ selectedPkg, authToken, onBack, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const pkg = PACKAGES.find(p => p.id === selectedPkg);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError('');
    setLoading(true);

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'Payment failed. Please try again.');
      setLoading(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        const res = await axios.post(
          '/api/users/me/purchase-tokens',
          { package: selectedPkg, paymentIntentId: paymentIntent.id },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        onSuccess(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Payment succeeded but credits could not be applied. Contact support@pubsilo.com with your payment ID: ' + paymentIntent.id);
        setLoading(false);
      }
    } else {
      setError('Payment did not complete. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '1.5rem' }}>
        <PaymentElement options={{
          layout: 'tabs',
          fields: { billingDetails: { address: { postalCode: 'auto' } } },
        }} />
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', borderRadius: '7px', marginBottom: '1rem', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', fontSize: '0.88rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          style={{ flex: '0 0 auto', padding: '0.85rem 1.25rem', background: 'transparent', border: '1px solid #ccc', borderRadius: '8px', fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', color: '#555' }}
        >
          ← Back
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          style={{
            flex: 1, padding: '0.85rem',
            backgroundColor: loading || !stripe ? '#6c757d' : '#007bff',
            color: '#fff', border: 'none', borderRadius: '8px',
            fontSize: '1rem', fontWeight: '600',
            cursor: loading || !stripe ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Processing…' : `Complete Purchase — ${pkg?.price}`}
        </button>
      </div>

      <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.78rem', marginTop: '0.75rem' }}>
        Powered by Stripe · Your card details are never sent to PubSilo
      </p>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────
function TokenPurchase() {
  const navigate = useNavigate();
  const { accessToken: token, clearSession } = useAuth();
  const username = localStorage.getItem('username');
  const userType = localStorage.getItem('userType');

  const [selectedPkg,        setSelectedPkg]        = useState('standard');
  const [clientSecret,       setClientSecret]       = useState(null);
  const [step1Loading,       setStep1Loading]        = useState(false);
  const [step1Error,         setStep1Error]          = useState('');
  const [result,             setResult]              = useState(null);
  const [termsChecked,       setTermsChecked]        = useState(false);
  const [purchaseAcknowledged, setPurchaseAcknowledged] = useState(false);
  const [isFirstPurchase,    setIsFirstPurchase]     = useState(false);
  const [promoCode,          setPromoCode]           = useState('');
  const [promoLoading,       setPromoLoading]        = useState(false);
  const [promoResult,        setPromoResult]         = useState(null);
  const [isMobile,           setIsMobile]            = useState(() => window.innerWidth < 768);
  // Stable nonce per purchase attempt — survives network retries, prevents double-charging
  const [purchaseNonce,      setPurchaseNonce]       = useState(() => Math.random().toString(36).slice(2) + Date.now().toString(36));

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if ((res.data.tokenPurchaseLog?.length ?? 0) === 0) setIsFirstPurchase(true); })
      .catch(() => {});
  }, [token]);

  if (!token || userType !== 'user') { navigate('/login'); return null; }

  const pkg = PACKAGES.find(p => p.id === selectedPkg);

  // Step 1: validate + create PaymentIntent → show Stripe form
  const handleStartPayment = async () => {
    setStep1Error('');
    if (!purchaseAcknowledged) { setStep1Error('Please check the acknowledgment box below before continuing.'); return; }
    if (isFirstPurchase && !termsChecked) { setStep1Error('Please accept the Terms of Service before your first purchase.'); return; }

    setStep1Loading(true);
    try {
      const res = await axios.post(
        '/api/users/me/create-payment-intent',
        {
          package: selectedPkg,
          acknowledged: true,
          idempotencyKey: purchaseNonce,
          ...(isFirstPurchase && termsChecked ? { acceptTerms: true } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClientSecret(res.data.clientSecret);
    } catch (err) {
      setStep1Error(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setStep1Loading(false);
    }
  };

  const handleSuccess = (data) => {
    setPurchaseAcknowledged(false);
    setClientSecret(null);
    setPurchaseNonce(Math.random().toString(36).slice(2) + Date.now().toString(36));
    const nextParam = new URLSearchParams(window.location.search).get('next');
    if (nextParam) { navigate(nextParam); return; }
    if (isFirstPurchase) setIsFirstPurchase(false);
    setResult({
      success: true,
      creditsAdded: data.tokensAdded,
      bonusTokens:  data.bonusTokens || 0,
      newBalance:   data.newBalance,
      amountCharged: data.amountCharged,
    });
    setTimeout(() => navigate(`/${username}`), 3000);
  };

  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const handlePromoRedeem = async (e) => {
    e.preventDefault();
    if (!promoCode.trim()) return;
    setPromoLoading(true); setPromoResult(null);
    try {
      const res = await axios.post('/api/users/me/redeem-promo', { code: promoCode.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setPromoResult({ success: true, tokensAwarded: res.data.tokensAwarded, promoBalance: res.data.promoBalance });
      setPromoCode('');
    } catch (err) {
      setPromoResult({ success: false, message: err.response?.data?.error || 'Invalid promo code.' });
    } finally {
      setPromoLoading(false);
    }
  };

  const T = { accent: '#dd3333', white: '#fff' };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f4', fontFamily: 'Arial, sans-serif' }}>

      {/* ── Navbar ── */}
      {isMobile ? (
        <nav style={{ display: 'flex', height: '70px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
          <div style={{ width: '50%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '80%', objectFit: 'contain' }} />
          </div>
          <div style={{ width: '50%', background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
            <div style={{ color: '#aaa', fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Purchase</div>
            <div style={{ color: T.white, fontSize: '20px', fontWeight: '300', letterSpacing: '0.04em' }}>Article Credits</div>
          </div>
        </nav>
      ) : (
        <nav style={{ background: '#13273F', display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <div style={{ color: '#aaa', fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Purchase</div>
            <div style={{ color: T.white, fontSize: '30px', fontWeight: '300', letterSpacing: '0.06em', lineHeight: 1 }}>Article Credits</div>
          </div>
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
            <Link to={`/${username}`} style={{ color: T.white, textDecoration: 'none', fontSize: '13px', fontWeight: '700', background: T.accent, padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>My Silo</Link>
            <button onClick={handleLogout} style={{ background: 'transparent', border: '1px solid #3a3a3a', color: '#aaa', fontSize: '12px', fontWeight: '600', padding: '6px 14px', borderRadius: '5px', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#888'; e.currentTarget.style.color = T.white; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#3a3a3a'; e.currentTarget.style.color = '#aaa'; }}
            >Log Out</button>
          </div>
        </nav>
      )}

      <div style={{ maxWidth: isMobile ? '100%' : '680px', margin: isMobile ? '1.25rem 0' : '3rem auto', padding: isMobile ? '0 1rem' : '0 1.5rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Purchase Article Credits</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>Article Credits are used to purchase and read articles on PubSilo.</p>

        {/* ── Success banner ── */}
        {result?.success && (
          <div style={{ padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' }}>
            <strong>Payment successful!</strong> {result.creditsAdded.toLocaleString()} Article Credits added to your account.
            {result.bonusTokens > 0 && <> Plus <strong>{result.bonusTokens} bonus credits</strong> for your first purchase!</>}
            <br />
            <span style={{ fontSize: '0.9rem' }}>New balance: <strong>{result.newBalance.toLocaleString()} credits</strong> · Charged: ${result.amountCharged?.toFixed(2)}</span>
            <br /><span style={{ fontSize: '0.85rem', color: '#1a7a30' }}>Redirecting to your Silo…</span>
          </div>
        )}

        {/* ── Step 1: Package selection + acknowledgments ── */}
        {!clientSecret && !result?.success && (
          <>
            {/* Package cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
              {PACKAGES.map(p => (
                <div key={p.id} onClick={() => setSelectedPkg(p.id)} style={{
                  flex: '1 1 160px', border: selectedPkg === p.id ? '2px solid #007bff' : '2px solid #ddd',
                  borderRadius: '10px', padding: '1.25rem 1rem', cursor: 'pointer',
                  backgroundColor: selectedPkg === p.id ? '#f0f7ff' : '#fff', textAlign: 'center',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}>
                  <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>{p.label}</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#007bff', lineHeight: 1.1 }}>{p.price}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0.4rem 0' }}>{p.credits.toLocaleString()} Article Credits</div>
                </div>
              ))}
            </div>

            {/* Promo code */}
            <div style={{ backgroundColor: '#faf5ff', border: '1px solid #e0d0f8', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🎁</span>
                <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#6a00cc' }}>Have a Promo Code?</h5>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.85rem' }}>Promotional Credits are added to your account and used automatically when purchasing articles — before your purchased credits.</p>
              {promoResult && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: '7px', marginBottom: '0.85rem', fontSize: '0.88rem', backgroundColor: promoResult.success ? '#e8f5e9' : '#fdecea', color: promoResult.success ? '#1b5e20' : '#721c24', border: `1px solid ${promoResult.success ? '#a5d6a7' : '#f5c6cb'}` }}>
                  {promoResult.success ? <><strong>🎉 Code redeemed!</strong> {promoResult.tokensAwarded} Promotional Credits added.</> : <><strong>Error:</strong> {promoResult.message}</>}
                </div>
              )}
              <form onSubmit={handlePromoRedeem} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.6rem' }}>
                <input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="Enter promo code"
                  style={{ flex: 1, padding: '0.6rem 0.85rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.95rem', letterSpacing: '0.05em', fontWeight: '600', textTransform: 'uppercase' }} />
                <button type="submit" disabled={promoLoading || !promoCode.trim()}
                  style={{ padding: '0.6rem 1.2rem', backgroundColor: promoLoading ? '#aaa' : '#6a00cc', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: promoLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                  {promoLoading ? 'Checking…' : 'Redeem'}
                </button>
              </form>
            </div>

            {/* Payment box */}
            <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <span style={{ fontSize: '1.1rem' }}>💳</span>
                <h5 style={{ margin: 0 }}>Payment</h5>
                <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#888' }}>🔒 Secure · Powered by Stripe</span>
              </div>

              {isFirstPurchase && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: '#eef6ff', border: '1.5px solid #4a90d9', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ fontSize: '1.3rem' }}>🎁</span>
                  <span style={{ fontSize: '0.88rem', color: '#0d3165', fontWeight: 600 }}>First purchase bonus — get <strong>40 extra Article Credits free</strong> with any package today.</span>
                </div>
              )}

              {isFirstPurchase && (
                <div style={{ marginBottom: '0.85rem', padding: '0.9rem 1rem', backgroundColor: '#f0f7ff', border: '1px solid #4a90d9', borderRadius: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', cursor: 'pointer', fontSize: '0.85rem', color: '#0d3165', lineHeight: '1.45' }}>
                    <input type="checkbox" checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)} style={{ marginTop: '2px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span>I have read and agree to PubSilo's{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Terms of Service</Link>{' '}and{' '}
                      <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Privacy Policy</Link>.
                    </span>
                  </label>
                </div>
              )}

              <div style={{ marginBottom: '1.25rem', padding: '1rem 1.1rem', backgroundColor: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', cursor: 'pointer', fontSize: '0.86rem', color: '#333', lineHeight: '1.55' }}>
                  <input type="checkbox" checked={purchaseAcknowledged} onChange={e => setPurchaseAcknowledged(e.target.checked)} style={{ marginTop: '3px', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }} />
                  <span>I understand and agree that: <strong>Article Credits are non-refundable</strong> once purchased; this charge will appear on my bank or credit card statement as <strong>"PUBSILO LLC- ARTICLE"</strong>; and this purchase is authorized and intentional.</span>
                </label>
              </div>

              {step1Error && (
                <div style={{ padding: '0.75rem 1rem', borderRadius: '7px', marginBottom: '1rem', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', fontSize: '0.88rem' }}>
                  {step1Error}
                </div>
              )}

              <button
                onClick={handleStartPayment}
                disabled={step1Loading || !purchaseAcknowledged || (isFirstPurchase && !termsChecked)}
                style={{
                  width: '100%', padding: '0.85rem',
                  backgroundColor: step1Loading || !purchaseAcknowledged || (isFirstPurchase && !termsChecked) ? '#6c757d' : '#007bff',
                  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600',
                  cursor: step1Loading || !purchaseAcknowledged || (isFirstPurchase && !termsChecked) ? 'not-allowed' : 'pointer',
                }}
              >
                {step1Loading ? 'Preparing payment…' : `Pay ${pkg?.price} for ${pkg?.credits.toLocaleString()} Article Credits`}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Stripe payment form ── */}
        {clientSecret && !result?.success && (
          <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Completing purchase</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#333' }}>{pkg?.label} — {pkg?.price} · {pkg?.credits.toLocaleString()} Article Credits</div>
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <StripePaymentForm
                selectedPkg={selectedPkg}
                authToken={token}
                onBack={() => { setClientSecret(null); setStep1Error(''); }}
                onSuccess={handleSuccess}
              />
            </Elements>
          </div>
        )}

        <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.8rem', marginTop: '1rem' }}>
          Article Credits are non-refundable. By completing this purchase you agree to PubSilo's{' '}
          <Link to="/terms" style={{ color: '#aaa' }}>Terms of Service</Link>.
        </p>

        {isMobile && (
          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingBottom: '2rem' }}>
            <Link to={`/${username}`} style={{ display: 'inline-block', padding: '0.75rem 2rem', backgroundColor: '#dd3333', color: '#fff', textDecoration: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.95rem' }}>
              ← Return to My Silo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default TokenPurchase;
